// =============================================================================
// iTravel 2.0 — App Providers
// =============================================================================
// Wraps the entire application with all required context providers.
// Note: Routing is handled by RouterProvider + createBrowserRouter in main.tsx,
// so we do NOT include BrowserRouter here.
// =============================================================================

import { CssBaseline, ThemeProvider } from '@mui/material';
import { QueryClientProvider } from '@tanstack/react-query';
import { lazy, Suspense, useEffect, useState, type ReactNode } from 'react';

import { useHydrateAuth } from '@/features/auth/hooks/useAuth';
import { useNativeSystemBars } from '@/hooks/useNativeSystemBars';
import { useResolvedTheme } from '@/hooks/useResolvedTheme';
import { queryClient } from '@/lib/api/query-client';

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface AppProvidersProps {
  children: ReactNode;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * AppProviders wraps the entire application with all required context providers.
 *
 * Provider order (outermost → innermost):
 * 1. QueryClientProvider — Server state management (TanStack Query)
 * 2. ThemeProvider + CssBaseline — MUI theming and CSS reset
 *
 * Note: Routing is handled separately via `<RouterProvider router={router} />`
 * in main.tsx. We do NOT wrap with BrowserRouter here because
 * `createBrowserRouter` provides its own context.
 *
 * Additional providers will be added as features are built:
 * - SyncProvider (Phase 2)
 * - SocketProvider (Phase 3)
 * - I18nProvider (Phase 6)
 */
export function AppProviders({ children }: AppProvidersProps) {
  // The client is module-level (see lib/api/query-client) because the offline
  // layer seeds it from SQLite when a session opens, which happens outside the
  // React tree.

  // Resolve the theme based on user preference (light / dark / auto).
  // When set to 'auto', this reacts to the OS prefers-color-scheme media query.
  const theme = useResolvedTheme();

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <CssBaseline enableColorScheme />
        <AuthHydrator />
        {children}
      </ThemeProvider>

      {/* React Query Devtools — only included in development builds */}
      {/* Lazy-loaded to avoid bundling in production */}
      {import.meta.env.DEV && (
        <Suspense fallback={null}>
          <ReactQueryDevtoolsLazy />
        </Suspense>
      )}
    </QueryClientProvider>
  );
}

// ---------------------------------------------------------------------------
// Auth Hydrator — validates persisted auth state on app startup
// ---------------------------------------------------------------------------
// This component renders nothing visible. It runs the useHydrateAuth effect
// which checks if persisted tokens are still valid and transitions the auth
// store from 'idle' → 'authenticated' or 'unauthenticated'. Without this,
// the AuthGuard stays stuck on "Checking authentication…" forever.
// ---------------------------------------------------------------------------

function AuthHydrator() {
  useHydrateAuth();
  useNativeSystemBars();
  return null;
}

// ---------------------------------------------------------------------------
// Lazy-loaded React Query Devtools (dev only, not in production bundle)
// ---------------------------------------------------------------------------

const ReactQueryDevtoolsLazyComponent = lazy(() =>
  import('@tanstack/react-query-devtools').then((mod) => ({
    default: mod.ReactQueryDevtools,
  })),
);

function ReactQueryDevtoolsLazy() {
  // Hide devtools on native mobile platforms (Capacitor) and narrow viewports
  // where they overlap the bottom navigation and aren't useful.
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    // Hide on native Capacitor platforms
    import('@capacitor/core')
      .then(({ Capacitor }) => {
        if (Capacitor.isNativePlatform()) setHidden(true);
      })
      .catch(() => {});

    // Hide on narrow viewports (mobile browser)
    const mql = window.matchMedia('(max-width: 768px)');
    const handleChange = (e: MediaQueryListEvent | MediaQueryList) =>
      setHidden((prev) => prev || e.matches);
    handleChange(mql);
    mql.addEventListener('change', handleChange);
    return () => mql.removeEventListener('change', handleChange);
  }, []);

  if (hidden) return null;

  return <ReactQueryDevtoolsLazyComponent initialIsOpen={false} buttonPosition="bottom-left" />;
}
